import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Upload, X, Loader2, FileText, Plus, Trash2, Pencil, CheckCircle2, AlertTriangle, Play } from 'lucide-react';
import { fusionPtrService } from '../../services/fusionPtrService';

let queueUid = 0;
const nextUid = () => `q${Date.now()}-${queueUid++}`;

/**
 * ExternalPtrImportModal — exterior PTR library entries (PDF/TXT).
 * - Queue: pick MULTIPLE files; nothing runs until the user presses Extraire.
 * - Extract: per-file (or all) AI extraction, each with its own status.
 * - Review: labeled fields (name, scope, org, per-measure category/priority/
 *   budget/responsable/deadline); confirm SAVES to the persisted library.
 * - Edit mode (editPtrId): loads a library entry for re-edit (PATCH on confirm).
 */
const ExternalPtrImportModal = ({ isOpen, onClose, onSaved, editPtrId = null }) => {
  const { t, i18n } = useTranslation();
  const fileRef = useRef(null);
  const [queue, setQueue] = useState([]);
  const [reviewUid, setReviewUid] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Edit mode: load the library entry as the review draft
  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    setSaving(false);
    if (editPtrId) {
      setQueue([]);
      setReviewUid('edit');
      setDraft(null);
      fusionPtrService.getExternalPtr(editPtrId)
        .then((response) => {
          const doc = response.data || {};
          setDraft({
            analysisId: String(doc._id || editPtrId),
            analysisName: doc.name || '',
            perimetre: doc.perimetre || { scope: '', missions: [], organizationName: '' },
            businessValues: doc.businessValues || [],
            supportAssets: doc.supportAssets || [],
            measures: (doc.measures || []).map((m, i) => ({
              key: `${doc._id}::${m.measureId || `m${i + 1}`}`,
              analysisId: String(doc._id || editPtrId),
              measureId: m.measureId || `m${i + 1}`,
              name: m.name || '',
              description: m.description || '',
              category: m.category || '',
              priority: m.priority || '',
              budget: m.budget || 0,
              responsiblePerson: m.responsiblePerson || '',
              deadline: m.deadline || '',
              businessValueName: m.businessValueName || '',
              supportAssets: m.supportAssets || [],
            })),
          });
        })
        .catch((e) => setError(e.message));
    } else {
      setReviewUid(null);
      setDraft(null);
    }
  }, [isOpen, editPtrId]);

  if (!isOpen) return null;

  const reset = () => {
    setQueue([]);
    setReviewUid(null);
    setDraft(null);
    setSaving(false);
    setError(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const addFiles = (fileList) => {
    const files = Array.from(fileList || []).filter((f) => /\.pdf$/i.test(f.name) || /\.txt$/i.test(f.name));
    if (!files.length) return;
    setQueue((q) => [
      ...q,
      ...files.slice(0, 10 - q.length).map((file) => ({
        uid: nextUid(),
        file,
        status: 'pending',
        error: null,
        draft: null,
      })),
    ]);
  };

  const extractOne = async (uid) => {
    const item = queue.find((q) => q.uid === uid);
    if (!item || item.status === 'extracting' || item.status === 'ready') return;
    setQueue((q) => q.map((x) => (x.uid === uid ? { ...x, status: 'extracting', error: null } : x)));
    try {
      const lang = (i18n.language || 'fr').startsWith('en') ? 'en' : 'fr';
      const response = await fusionPtrService.importExternalPtr(item.file, lang);
      const snapshot = response.data?.snapshot;
      if (!snapshot) throw new Error(t('fusionPtr.importEmpty', 'Extraction vide — vérifiez le fichier.'));
      setQueue((q) => q.map((x) => (x.uid === uid ? { ...x, status: 'ready', draft: snapshot } : x)));
    } catch (e) {
      setQueue((q) => q.map((x) => (x.uid === uid ? { ...x, status: 'error', error: e.message } : x)));
    }
  };

  const extractAll = () => {
    queue.filter((x) => x.status === 'pending' || x.status === 'error').forEach((x) => extractOne(x.uid));
  };

  const openReview = (uid) => {
    const item = queue.find((q) => q.uid === uid);
    if (!item?.draft) return;
    setDraft(JSON.parse(JSON.stringify(item.draft)));
    setReviewUid(uid);
    setError(null);
  };

  const setField = (path, value) => {
    setDraft((d) => {
      const next = JSON.parse(JSON.stringify(d));
      const keys = path.split('.');
      let node = next;
      for (let i = 0; i < keys.length - 1; i += 1) node = node[keys[i]];
      node[keys[keys.length - 1]] = value;
      return next;
    });
  };

  const setMeasure = (idx, field, value) => {
    setDraft((d) => {
      const next = JSON.parse(JSON.stringify(d));
      next.measures[idx] = { ...next.measures[idx], [field]: value };
      return next;
    });
  };

  const removeMeasure = (idx) => {
    setDraft((d) => {
      const next = JSON.parse(JSON.stringify(d));
      next.measures.splice(idx, 1);
      return next;
    });
  };

  const addMeasure = () => {
    setDraft((d) => {
      const next = JSON.parse(JSON.stringify(d));
      const n = next.measures.length + 1;
      const measureId = `m${n}`;
      next.measures.push({
        key: `${next.analysisId}::${measureId}`,
        analysisId: next.analysisId,
        measureId,
        name: '',
        description: '',
        source: 'custom',
        category: '',
        priority: '',
        budget: 0,
        responsiblePerson: '',
        deadline: '',
      });
      return next;
    });
  };

  const handleConfirm = async () => {
    if (!draft || !(draft.measures || []).length || saving) return;
    setSaving(true);
    setError(null);
    try {
      let saved;
      if (editPtrId) {
        const response = await fusionPtrService.updateExternalPtr(editPtrId, draft);
        saved = response.data;
      } else {
        const response = await fusionPtrService.saveExternalPtr(draft);
        saved = response.data;
      }
      if (reviewUid && reviewUid !== 'edit') {
        setQueue((q) => q.filter((x) => x.uid !== reviewUid));
      }
      setReviewUid(null);
      setDraft(null);
      onSaved(saved);
      if (editPtrId) handleClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const statusBadge = (item) => {
    if (item.status === 'extracting') {
      return <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-blue-600"><Loader2 size={12} className="animate-spin" />{t('fusionPtr.importExtracting', 'Extraction IA en cours...')}</span>;
    }
    if (item.status === 'ready') {
      return <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-green-700"><CheckCircle2 size={12} />{t('fusionPtr.importReady', 'Prêt à relire ({{count}} mesures)', { count: (item.draft?.measures || []).length })}</span>;
    }
    if (item.status === 'error') {
      return <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-red-600"><AlertTriangle size={12} />{item.error}</span>;
    }
    return <span className="text-[11px] font-medium text-slate-500">{t('fusionPtr.importPending', 'En attente')}</span>;
  };

  const inReview = reviewUid !== null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 sm:p-6" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={handleClose} aria-hidden="true" />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl flex flex-col overflow-hidden border border-slate-200 max-h-[90vh]">
        <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-slate-50 to-blue-50/40 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-blue-100 border border-blue-200 flex items-center justify-center text-blue-600">
              <Upload size={18} />
            </div>
            <div>
              <h3 className="text-sm font-bold text-slate-800">
                {editPtrId
                  ? t('fusionPtr.importEditTitle', 'Modifier le PTR externe')
                  : t('fusionPtr.importExternalTitle', 'Importer un PTR externe (PDF/TXT)')}
              </h3>
              <p className="text-xs text-slate-500">
                {t('fusionPtr.importExternalSubtitle', "L'IA extrait périmètre, biens supports et mesures — vérifiez avant d'ajouter")}
              </p>
            </div>
          </div>
          <button type="button" onClick={handleClose} aria-label={t('common.close', 'Fermer')} className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-200/60">
            <X size={18} />
          </button>
        </div>

        <div className="p-6 overflow-y-auto space-y-4">
          {error && (
            <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>
          )}

          {!inReview && !editPtrId && (
            <>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  className="flex-1 border-2 border-dashed border-slate-300 hover:border-blue-400 rounded-xl p-5 text-center transition-colors"
                >
                  <FileText size={24} className="mx-auto text-slate-400 mb-1" />
                  <p className="text-sm font-semibold text-slate-700">{t('fusionPtr.importPickFiles', 'Choisir un ou plusieurs fichiers .pdf / .txt (max 10 Mo chacun)')}</p>
                  <input
                    ref={fileRef}
                    type="file"
                    multiple
                    accept=".pdf,.txt,application/pdf,text/plain"
                    className="hidden"
                    onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
                  />
                </button>
                {queue.some((x) => x.status === 'pending' || x.status === 'error') && (
                  <button
                    type="button"
                    onClick={extractAll}
                    className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg flex-shrink-0"
                  >
                    <Play size={13} />
                    {t('fusionPtr.importExtractAll', 'Tout extraire')}
                  </button>
                )}
              </div>

              {queue.length > 0 && (
                <ul className="space-y-2">
                  {queue.map((item) => (
                    <li key={item.uid} className="flex items-center gap-3 border border-slate-200 rounded-lg px-3 py-2 bg-slate-50/60">
                      <FileText size={16} className="text-slate-400 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-semibold text-slate-700 truncate">{item.file.name}</p>
                        {statusBadge(item)}
                      </div>
                      {item.status === 'ready' && (
                        <button type="button" onClick={() => openReview(item.uid)} className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg flex-shrink-0">
                          <Pencil size={11} />
                          {t('fusionPtr.importReview', 'Relire')}
                        </button>
                      )}
                      {(item.status === 'pending' || item.status === 'error') && (
                        <button type="button" onClick={() => extractOne(item.uid)} className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg flex-shrink-0">
                          <Play size={11} />
                          {t('fusionPtr.importExtract', 'Extraire')}
                        </button>
                      )}
                      <button type="button" onClick={() => setQueue((q) => q.filter((x) => x.uid !== item.uid))} className="p-1.5 rounded text-slate-400 hover:text-red-600 hover:bg-red-50 flex-shrink-0" title={t('common.delete', 'Supprimer')}>
                        <Trash2 size={14} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}

          {inReview && draft && (
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">{t('fusionPtr.importName', 'Nom de la source')}</label>
                <input
                  type="text"
                  value={draft.analysisName || ''}
                  onChange={(e) => setField('analysisName', e.target.value)}
                  className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">{t('fusionPtr.importScope', 'Périmètre')}</label>
                <textarea
                  value={draft.perimetre?.scope || ''}
                  onChange={(e) => setField('perimetre.scope', e.target.value)}
                  rows={3}
                  className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">{t('fusionPtr.importOrg', 'Organisation')}</label>
                <input
                  type="text"
                  value={draft.perimetre?.organizationName || ''}
                  onChange={(e) => setField('perimetre.organizationName', e.target.value)}
                  className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-semibold text-slate-700">
                    {t('fusionPtr.importMeasures', 'Mesures ({{count}})', { count: (draft.measures || []).length })}
                  </label>
                  <button type="button" onClick={addMeasure} className="inline-flex items-center gap-1 text-xs font-semibold text-blue-600 hover:text-blue-800">
                    <Plus size={13} /> {t('common.add', 'Ajouter')}
                  </button>
                </div>
                <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                  {(draft.measures || []).map((m, i) => (
                    <fieldset key={m.measureId || i} className="border border-slate-200 rounded-lg p-2.5 space-y-1.5 bg-slate-50/60">
                      <div className="flex items-center gap-2">
                        <div className="flex-1">
                          <label className="block text-[11px] font-semibold text-slate-600 mb-0.5">{t('fusionPtr.importFieldName', 'Nom')}</label>
                          <input
                            type="text"
                            value={m.name || ''}
                            onChange={(e) => setMeasure(i, 'name', e.target.value)}
                            placeholder={t('fusionPtr.importMeasureName', 'Nom de la mesure')}
                            className="w-full text-sm border border-slate-300 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <button type="button" onClick={() => removeMeasure(i)} className="mt-4 p-1.5 rounded text-slate-400 hover:text-red-600 hover:bg-red-50" title={t('common.delete', 'Supprimer')}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                      <div>
                        <label className="block text-[11px] font-semibold text-slate-600 mb-0.5">{t('fusionPtr.importFieldDesc', 'Description')}</label>
                        <textarea
                          value={m.description || ''}
                          onChange={(e) => setMeasure(i, 'description', e.target.value)}
                          placeholder={t('fusionPtr.importMeasureDesc', 'Description')}
                          rows={2}
                          className="w-full text-xs border border-slate-300 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                        <div>
                          <label className="block text-[11px] font-semibold text-slate-600 mb-0.5">{t('fusionPtr.importFieldCategory', 'Catégorie')}</label>
                          <input type="text" value={m.category || ''} onChange={(e) => setMeasure(i, 'category', e.target.value)} className="w-full text-xs border border-slate-300 rounded-lg px-2 py-1.5" />
                        </div>
                        <div>
                          <label className="block text-[11px] font-semibold text-slate-600 mb-0.5">{t('fusionPtr.importFieldPriority', 'Priorité')}</label>
                          <input type="text" value={m.priority || ''} onChange={(e) => setMeasure(i, 'priority', e.target.value)} className="w-full text-xs border border-slate-300 rounded-lg px-2 py-1.5" />
                        </div>
                        <div>
                          <label className="block text-[11px] font-semibold text-slate-600 mb-0.5">{t('fusionPtr.importFieldBudget', 'Budget (€)')}</label>
                          <input type="number" value={m.budget || 0} onChange={(e) => setMeasure(i, 'budget', Number(e.target.value) || 0)} className="w-full text-xs border border-slate-300 rounded-lg px-2 py-1.5" />
                        </div>
                        <div>
                          <label className="block text-[11px] font-semibold text-slate-600 mb-0.5">{t('fusionPtr.importFieldOwner', 'Responsable')}</label>
                          <input type="text" value={m.responsiblePerson || ''} onChange={(e) => setMeasure(i, 'responsiblePerson', e.target.value)} className="w-full text-xs border border-slate-300 rounded-lg px-2 py-1.5" />
                        </div>
                        <div>
                          <label className="block text-[11px] font-semibold text-slate-600 mb-0.5">{t('fusionPtr.importFieldDeadline', 'Échéance')}</label>
                          <input type="text" value={m.deadline || ''} onChange={(e) => setMeasure(i, 'deadline', e.target.value)} className="w-full text-xs border border-slate-300 rounded-lg px-2 py-1.5" />
                        </div>
                        <div>
                          <label className="block text-[11px] font-semibold text-slate-600 mb-0.5">{t('fusionPtr.importFieldAsset', 'Bien support')}</label>
                          <input type="text" value={(m.supportAssets || [])[0] || ''} onChange={(e) => setMeasure(i, 'supportAssets', e.target.value ? [e.target.value] : [])} className="w-full text-xs border border-slate-300 rounded-lg px-2 py-1.5" />
                        </div>
                      </div>
                    </fieldset>
                  ))}
                </div>
              </div>
            </div>
          )}
          {inReview && !draft && !error && (
            <div className="flex items-center justify-center gap-2 py-10 text-sm text-slate-500">
              <Loader2 size={18} className="animate-spin" />
              {t('common.loading', 'Chargement...')}
            </div>
          )}
        </div>

        {inReview && draft && (
          <div className="flex justify-between gap-3 px-6 py-4 border-t border-slate-200 bg-slate-50">
            {!editPtrId && (
              <button type="button" onClick={() => { setReviewUid(null); setDraft(null); }} className="px-4 py-2 text-xs font-medium text-slate-600 hover:text-slate-800 bg-white border border-slate-300 rounded-lg">
                {t('fusionPtr.importBackToQueue', 'Retour à la file')}
              </button>
            )}
            <div className="flex gap-3 ml-auto">
              <button type="button" onClick={handleClose} className="px-4 py-2 text-xs font-medium text-slate-600 hover:text-slate-800 bg-white border border-slate-300 rounded-lg">
                {t('common.cancel', 'Annuler')}
              </button>
              <button
                type="button"
                onClick={handleConfirm}
                disabled={!(draft?.measures || []).length || saving}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50"
              >
                {saving && <Loader2 size={13} className="animate-spin" />}
                {editPtrId
                  ? t('fusionPtr.importSaveChanges', 'Enregistrer')
                  : t('fusionPtr.importConfirm', 'Ajouter comme source')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
};

ExternalPtrImportModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
  editPtrId: PropTypes.string,
};

export default ExternalPtrImportModal;
